/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { leaderApi } from '../../../api/leaderApi';
import { toLocalDateStr, checkRecurringOverlap, formatRange } from '../../../utils/leaderUtils';
import { useSafeClose } from '../../../hooks/useSafeClose';

const STATUS_OPTIONS = [
    { value: 'urlaub', label: 'Urlaub', color: '#007aff', bg: '#ddf4ff' },
    { value: 'krank', label: 'Krankheit', color: '#ff3b30', bg: '#fde8e8' },
    { value: 'termin', label: 'Termin', color: '#ff9500', bg: '#fff5e6' },
    { value: 'sonstiges', label: 'Sonstiges', color: '#af52de', bg: '#f3e8ff' }
];

const RECURRENCE_OPTIONS = [
    { value: 'none', label: 'Einmalig' },
    { value: 'daily', label: 'Täglich' },
    { value: 'weekly', label: 'Wöchentlich' },
    { value: 'monthly', label: 'Monatlich' },
    { value: 'yearly', label: 'Jährlich' }
];

// ========================================
// HELPERS
// ========================================
function formatGermanDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatDisplayRange(start, end) {
    if (!start) return '';
    
    // Normalize to date-only strings for comparison (Support 'T' and ' ')
    const sStr = start.split(/[T ]/)[0];
    const eStr = end ? end.split(/[T ]/)[0] : sStr;
    
    if (!end || sStr === eStr) return formatGermanDate(sStr);
    
    const dStart = new Date(sStr);
    const dEnd = new Date(eStr);
    
    // Final safety check if Date objects represent same day
    if (dStart.toISOString().split('T')[0] === dEnd.toISOString().split('T')[0]) {
        return formatGermanDate(start);
    }

    const startStr = dStart.toLocaleDateString('de-DE', { day: 'numeric' });
    const endStr = dEnd.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
    if (dStart.getMonth() === dEnd.getMonth() && dStart.getFullYear() === dEnd.getFullYear()) {
        return `${startStr}. bis ${endStr}`;
    }
    return `${formatGermanDate(start)} bis ${formatGermanDate(end)}`;
}

function parseGermanDate(str) {
    if (!str) return null;
    const parts = str.split(/[.\/ -]+/).filter(Boolean);
    if (parts.length < 2) return null;
    let d = parts[0].padStart(2, '0');
    let m = parts[1].padStart(2, '0');
    let y = parts[2] || new Date().getFullYear().toString();
    if (y.length === 2) y = '20' + y;
    const ds = `${y}-${m}-${d}`;
    return isNaN(new Date(ds).getTime()) ? null : ds;
}

// ========================================
// DATE RANGE PICKER COMPONENT
// ========================================
function DateRangePicker({ startDate, endDate, absences = [], onRangeChange }) {
    const [viewDate, setViewDate] = useState(new Date());
    const month = viewDate.getMonth();
    const year = viewDate.getFullYear();

    // Synchronize view with selected start date when it changes (e.g. when selecting an entry from the list)
    useEffect(() => {
        if (startDate) {
            const d = new Date(startDate);
            if (!isNaN(d.getTime())) {
                // Only jump if the selected date is not in the current visible month/year
                if (d.getMonth() !== month || d.getFullYear() !== year) {
                    setViewDate(new Date(d.getFullYear(), d.getMonth(), 1));
                }
            }
        }
    }, [startDate]);

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const startOffset = (firstDay === 0 ? 6 : firstDay - 1);

    const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
    const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

    const handleDateClick = (d) => {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        
        if (!startDate || (startDate && endDate)) {
            onRangeChange(dateStr, null);
        } else {
            if (dateStr === startDate) {
                onRangeChange(startDate, null);
            } else if (dateStr < startDate) {
                onRangeChange(dateStr, startDate);
            } else {
                onRangeChange(startDate, dateStr);
            }
        }
    };

    const isSelected = (d) => {
        const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        return ds === startDate || ds === endDate;
    };

    const isInRange = (d) => {
        if (!startDate || !endDate) return false;
        const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        return ds > startDate && ds < endDate;
    };

    // Find all absences for a day
    const getAbsencesForDay = (day) => {
        const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return absences.filter(a => {
            const start = a.start_datetime.split(/[T ]/)[0];
            const end = a.end_datetime.split(/[T ]/)[0];
            return ds >= start && ds <= end;
        });
    };

    return (
        <div className="apple-range-picker" style={{ padding: '10px' }}>
            <div className="range-picker-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <button className="apple-btn-ghost" onClick={prevMonth} style={{ width: '24px', height: '24px', fontSize: '11px' }}>‹</button>
                <div className="range-picker-month-name" style={{ fontSize: '13px', fontWeight: '700' }}>{viewDate.toLocaleString('de-DE', { month: 'long', year: 'numeric' })}</div>
                <button className="apple-btn-ghost" onClick={nextMonth} style={{ width: '24px', height: '24px', fontSize: '11px' }}>›</button>
            </div>
            <div className="range-picker-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
                {['M', 'D', 'M', 'D', 'F', 'S', 'S'].map((day, i) => (
                    <div key={i} className="range-picker-day-label" style={{ textAlign: 'center', fontSize: '10px', fontWeight: '800', color: '#86868b' }}>{day}</div>
                ))}
                {[...Array(startOffset)].map((_, i) => <div key={`empty-${i}`} className="range-picker-day empty" />)}
                {[...Array(daysInMonth)].map((_, i) => {
                    const day = i + 1;
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const isStart = startDate && dateStr === startDate;
                    const isEnd = endDate && dateStr === endDate;
                    const isSingle = startDate && !endDate && isStart;
                    const now_ = new Date();
                    const today = `${now_.getFullYear()}-${String(now_.getMonth() + 1).padStart(2, '0')}-${String(now_.getDate()).padStart(2, '0')}`;
                    
                    const dayAbs = getAbsencesForDay(day);
                    const firstAbs = dayAbs[0];
                    const statusColor = firstAbs ? (STATUS_OPTIONS.find(s => s.value === (firstAbs.absence_type || '').toLowerCase())?.color || '#ccc') : null;
                    const isSelectedDay = isSelected(day) || isInRange(day);

                    return (
                        <div 
                            key={day} 
                            className={`range-picker-day ${isSelected(day) ? 'selected' : ''} ${isInRange(day) ? 'in-range' : ''} ${isStart && endDate ? 'range-start' : ''} ${isEnd ? 'range-end' : ''} ${isSingle ? 'single-day' : ''} ${dateStr === today ? 'today' : ''}`}
                            onClick={() => handleDateClick(day)}
                            style={statusColor && !isSelectedDay ? { 
                                background: `${statusColor}15`, // Very light version of the color
                                color: statusColor,
                                fontWeight: '700'
                            } : {}}
                        >
                            {day}
                            {!isSelectedDay && dayAbs.length > 0 && (
                                <div style={{ position: 'absolute', bottom: '2px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '2px', justifyContent: 'center' }}>
                                    {dayAbs.map((abs, idx) => {
                                        const c = STATUS_OPTIONS.find(s => s.value === (abs.absence_type || '').toLowerCase())?.color || '#ccc';
                                        return <div key={idx} style={{ width: '3px', height: '3px', borderRadius: '50%', background: c }} />;
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="range-picker-hint" style={{ fontSize: '10px', color: '#86868b', marginTop: '6px', textAlign: 'center' }}>Wähle Start- und Enddatum</div>
        </div>
    );
}

// ========================================
// MAIN MODAL COMPONENT
// ========================================
/**
 * AbsenceDetailModal Component
 * Displays and manages absences for a specific worker.
 * 
 * @param {Object} props - Component properties.
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {Function} props.onClose - Callback to close the modal.
 * @param {string|number} props.workerId - ID of the worker being managed.
 * @param {Array} props.workers - List of all employees.
 * @param {Array} props.assignments - List of assignments for duration/conflict context.
 * @param {string|number} [props.initialAbsenceId] - Optional ID to focus on a specific absence.
 * @param {Function} props.onUpdate - Callback after data changes.
 * @param {boolean} [props.showBackToLeader=false] - If true, shows a 'Back to Leader' button.
 * @returns {JSX.Element|null} The rendered modal or null.
 */
export default function AbsenceDetailModal({
    isOpen,
    onClose,
    workerId,
    workers = [],
    assignments = [],
    initialAbsenceId,
    onUpdate,
    showBackToLeader = false
}) {
    const navigate = useNavigate();
    const worker = workers.find(w => String(w.id) === String(workerId));
    const [absenceType, setAbsenceType] = useState('urlaub');
    const [hoursPerDay, setHoursPerDay] = useState(8);
    const [status, setStatus] = useState('genehmigt');
    const [notes, setNotes] = useState('');

    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    const [absences, setAbsences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const initialForm = {
        absence_type: 'urlaub',
        startDate: '',
        endDate: '',
        startTime: '08:00',
        endTime: '16:00',
        notes: '',
        all_day: true,
        recurrence_type: 'none'
    };

    const [form, setForm] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);
    const [showArchive, setShowArchive] = useState(false);
    const [dateInput, setDateInput] = useState('');

    useEffect(() => {
        setDateInput(formatDisplayRange(form.startDate, form.endDate));
    }, [form.startDate, form.endDate]);

    const handleDateInputBlur = () => {
        const parts = dateInput.split(/bis| - | bis /i).map(p => p.trim());
        if (!parts[0]) return;
        
        const start = parseGermanDate(parts[0]);
        const end = parts[1] ? parseGermanDate(parts[1]) : null;
        
        if (start) {
            setForm(prev => ({ ...prev, startDate: start, endDate: end || null }));
        } else {
            // Reset display if invalid
            setDateInput(formatDisplayRange(form.startDate, form.endDate));
        }
    };
    
    useEffect(() => {
        if (isOpen && workerId) {
            loadAbsences();

            // IMPORTANT: If we open the modal without a specific initialAbsenceId,
            // we must ensure any previous internal editing state is cleared.
            if (!initialAbsenceId) {
                handleReset();
            }

            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = '';
            };
        }
    }, [isOpen, workerId, initialAbsenceId]);

    // Handle initial entry from calendar navigation
    useEffect(() => {
        if (isOpen && initialAbsenceId && absences.length > 0) {
            const target = absences.find(a => String(a.id) === String(initialAbsenceId));
            if (target) {
                handleEditStart(target);
                // Ensure archive is open if selecting a past entry
                const endStr = target.end_datetime.split(/[T ]/)[0];
                const end = new Date(endStr);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (end < today) setShowArchive(true);
            }
        }
    }, [isOpen, initialAbsenceId, absences]);

    const loadAbsences = async () => {
        setLoading(true);
        try {
            // Fetch all absences for this worker (limited set or no filter to get recent history)
            const data = await leaderApi.getWorkerAbsences(workerId);
            setAbsences(data || []);
            return data || [];
        } catch (err) {
            console.error('Error loading absences:', err);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const preparePayload = (f) => {
        // Ensure we only have the date part (YYYY-MM-DD)
        let start = (f.startDate || '').split(/[T ]/)[0];
        let end = (f.endDate || f.startDate || '').split(/[T ]/)[0];
        
        if (!start) return null;

        if (f.all_day) {
            start += 'T00:00:00';
            end += 'T23:59:59';
        } else {
            start += `T${f.startTime}:00`;
            end += `T${f.endTime}:00`;
        }

        return {
            absence_type: f.absence_type,
            recurrence_type: f.recurrence_type,
            notes: f.notes,
            all_day: f.all_day,
            start_datetime: start,
            end_datetime: end
        };
    };

    const handleReset = () => {
        setForm(initialForm);
        setEditingId(null);
    };

    const handleSubmit = async () => {
        if (!form.startDate) {
            alert('Bitte wählen Sie mindestens ein Datum aus.');
            return;
        }

        setIsSaving(true);
        try {
            const payload = preparePayload(form);
            
            // Conflict Check
            const manualConflicts = assignments.filter(a => {
                const hasWorker = a.workers?.some(aw => String(aw.worker_id || aw.id) === String(workerId));
                return hasWorker && checkRecurringOverlap(payload, a);
            });

            if (manualConflicts.length > 0) {
                const details = manualConflicts.map(c => `"${c.project_name || 'Unbenanntes Projekt'}" am ${formatRange(c)}`).join('\n- ');
                if (!window.confirm(`Achtung: Konflikt mit Projekten:\n\n- ${details}\n\nTrotzdem speichern?`)) {
                    setIsSaving(false); return;
                }
            }

            if (editingId) {
                await leaderApi.updateAbsence(editingId, payload);
            } else {
                await leaderApi.createWorkerAbsence(workerId, payload);
            }
            
            handleReset();
            await loadAbsences();
            if (onUpdate) onUpdate();
        } catch (err) {
            alert('Fehler beim Speichern.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditStart = (abs) => {
        // Normalize database timestamps to date strings for the form
        const start = abs.start_datetime.split(/[T ]/)[0];
        const end = abs.end_datetime.split(/[T ]/)[0];
        
        // Extract time parts (HH:mm)
        const startTime = (abs.start_datetime.split(/[T ]/)[1] || '08:00').substring(0, 5);
        const endTime = (abs.end_datetime.split(/[T ]/)[1] || '16:00').substring(0, 5);

        setForm({
            absence_type: abs.absence_type,
            startDate: start,
            endDate: end === start ? null : end,
            startTime,
            endTime: (end === start && abs.all_day) ? '16:00' : endTime,
            notes: abs.notes || '',
            all_day: abs.all_day,
            recurrence_type: abs.recurrence_type || 'none'
        });
        setEditingId(abs.id);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Diese Abwesenheit wirklich löschen?')) return;
        try {
            await leaderApi.deleteAbsence(id);
            await loadAbsences();
            if (onUpdate) onUpdate();
        } catch (err) {
            alert('Fehler beim Löschen.');
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="apple-modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="apple-modal wide" onClick={e => e.stopPropagation()} style={{ borderRadius: '28px', overflow: 'hidden', height: 'min(800px, 90vh)' }}>
                <div className="apple-modal-header" style={{ padding: '16px 24px', background: 'white', borderBottom: '1px solid #f2f2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div className="apple-avatar" style={{ background: worker?.color || getAvatarColor(worker?.name || ''), width: '40px', height: '40px', fontSize: '15px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700' }}>
                            {getInitials(worker?.name || '')}
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '800' }}>Abwesenheiten: {worker?.name}</h2>
                            <p style={{ margin: 0, color: '#86868b', fontSize: '12px' }}>{worker?.category_name || 'Mitarbeiter'}</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {showBackToLeader && (
                            <button 
                                className="apple-btn-secondary" 
                                onClick={() => navigate('/tools/leader')} 
                                style={{ height: '28px', padding: '0 10px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                                <span>🔙</span> Zurück zum Kolonnenführer
                            </button>
                        )}
                        <button className="apple-btn-ghost" onClick={onClose} style={{ width: '28px', height: '28px', padding: 0 }}>╳</button>
                    </div>
                </div>

                <div className="apple-modal-grid-body">
                    {/* LEFT COLUMN: LIST */}
                    <div className="absence-list-column">
                        
                        {loading ? (
                            <div style={{ padding: '40px', textAlign: 'center', color: '#86868b' }}>Laden...</div>
                        ) : (
                            <>
                                {(() => {
                                    const todayMidnight = new Date();
                                    todayMidnight.setHours(0, 0, 0, 0);
                                    
                                    // An entry is "upcoming/active" if it hasn't finished yet or starts in the future
                                    const upcoming = absences.filter(a => new Date(a.end_datetime) >= todayMidnight);
                                    const past = absences.filter(a => new Date(a.end_datetime) < todayMidnight);
                                    
                                    return (
                                        <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <h3 style={{ fontSize: '14px', color: '#86868b', margin: 0, fontWeight: '600' }}>Geplante Abwesenheiten</h3>
                                                <div style={{ fontSize: '12px', color: '#86868b' }}>{upcoming.length} Einträge</div>
                                            </div>

                                            {upcoming.length === 0 ? (
                                                <div style={{ padding: '40px', textAlign: 'center', color: '#86868b', background: 'white', borderRadius: '20px', border: '1px solid rgba(0,0,0,0.05)' }}>
                                                    Keine geplanten Abwesenheiten.
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    {upcoming.map(abs => {
                                                        const isActive = editingId === abs.id;
                                                        const status = STATUS_OPTIONS.find(s => s.value === (abs.absence_type || '').toLowerCase()) || STATUS_OPTIONS[0];

                                                        return (
                                                            <div 
                                                                key={abs.id} 
                                                                className={`apple-worker-card ${isActive ? 'active' : ''}`} 
                                                                style={{ 
                                                                    padding: '12px 16px', 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    justifyContent: 'space-between', 
                                                                    borderRadius: '16px', 
                                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                    position: 'relative',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={() => handleEditStart(abs)}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                                                    <div className="apple-status-dot" style={{ backgroundColor: status.color, width: '10px', height: '10px', borderRadius: '50%' }} />
                                                                    <div>
                                                                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#1d1d1f' }}>
                                                                            {formatRange({ ...abs, start_datetime: abs.start_datetime, end_datetime: abs.end_datetime })}
                                                                        </div>
                                                                        <div style={{ fontSize: '12px', color: '#86868b', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                                            <span>{status.label}</span>
                                                                            {abs.recurrence_type && abs.recurrence_type !== 'none' && <span style={{ background: 'rgba(0,0,0,0.05)', padding: '1px 5px', borderRadius: '4px', fontSize: '10px' }}>🔄 {abs.recurrence_type}</span>}
                                                                        </div>
                                                                        {abs.notes && <div className="absence-notes-display" style={{ marginTop: '2px', fontSize: '11px' }}>📝 {abs.notes}</div>}
                                                                    </div>
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                                    <button className="apple-btn-ghost" onClick={(e) => { e.stopPropagation(); handleEditStart(abs); }} style={{ width: '28px', height: '28px', padding: 0 }}>✏️</button>
                                                                    <button className="apple-btn-danger" onClick={(e) => { e.stopPropagation(); handleDelete(abs.id); }} style={{ width: '28px', height: '28px', padding: 0 }}>🗑️</button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {past.length > 0 && (
                                                <div style={{ marginTop: '16px' }}>
                                                    <button 
                                                        className="apple-btn-ghost" 
                                                        onClick={() => setShowArchive(!showArchive)}
                                                        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderRadius: '12px', fontSize: '12px', color: '#86868b' }}
                                                    >
                                                        <span>Archiv ({past.length} vergangene Einträge)</span>
                                                        <span>{showArchive ? '▲' : '▼'}</span>
                                                    </button>
                                                    
                                                    {showArchive && (
                                                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                            {past.map(abs => {
                                                                const isActive = editingId === abs.id;
                                                                const status = STATUS_OPTIONS.find(s => s.value === (abs.absence_type || '').toLowerCase()) || STATUS_OPTIONS[0];
                                                                return (
                                                                    <div 
                                                                        key={abs.id} 
                                                                        className={`apple-worker-card ${isActive ? 'active' : ''}`} 
                                                                        style={{ 
                                                                            padding: '10px 16px', 
                                                                            opacity: 0.75, 
                                                                            background: isActive ? `${status.color}15` : `${status.color}08`,
                                                                            cursor: 'pointer',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                            borderRadius: '12px',
                                                                            position: 'relative',
                                                                            border: isActive ? `1px solid ${status.color}30` : '1px solid transparent'
                                                                        }}
                                                                        onClick={() => handleEditStart(abs)}
                                                                    >
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                            <div className="apple-status-dot" style={{ backgroundColor: status.color, width: '8px', height: '8px', opacity: 0.6 }} />
                                                                            <div style={{ fontSize: '13px' }}>
                                                                                <div style={{ fontWeight: '600', color: '#1d1d1f' }}>{formatRange(abs)}</div>
                                                                                <div style={{ fontSize: '11px', color: '#86868b' }}>{status.label}</div>
                                                                            </div>
                                                                        </div>
                                                                        <div style={{ display: 'flex', gap: '4px' }}>
                                                                            <button className="apple-btn-ghost" onClick={(e) => { e.stopPropagation(); handleEditStart(abs); }} style={{ width: '24px', height: '24px', padding: 0 }}>✏️</button>
                                                                            <button className="apple-btn-danger" onClick={(e) => { e.stopPropagation(); handleDelete(abs.id); }} style={{ width: '24px', height: '24px', padding: 0 }}>🗑️</button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </>
                        )}
                    </div>

                    {/* RIGHT COLUMN: FORM */}
                    <div className="absence-form-column" style={{ padding: '12px' }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700' }}>
                            {editingId ? 'Abwesenheit bearbeiten' : 'Neue Abwesenheit'}
                        </h3>

                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                            {/* Inner Left: Calendar (Flexible) */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <DateRangePicker 
                                    startDate={form.startDate} 
                                    endDate={form.endDate}
                                    absences={absences}
                                    onRangeChange={(s, e) => setForm({ ...form, startDate: s, endDate: e })} 
                                />
                            </div>

                            {/* Inner Right: Core Fields (Flexible) */}
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div>
                                    <label className="apple-label">Zeitraum</label>
                                    <input 
                                        className="apple-input" 
                                        value={dateInput} 
                                        onChange={e => setDateInput(e.target.value)} 
                                        onBlur={handleDateInputBlur}
                                        onKeyDown={e => e.key === 'Enter' && handleDateInputBlur()}
                                        placeholder="z.B. 10.03.2026"
                                        style={{ background: 'white', border: '1px solid #d2d2d7' }}
                                    />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <div>
                                        <label className="apple-label">Typ</label>
                                        <select className="apple-input" value={form.absence_type} onChange={e => setForm({ ...form, absence_type: e.target.value })} style={{ padding: '0 8px', fontSize: '13px' }}>
                                            {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="apple-label">Wiederholung</label>
                                        <select className="apple-input" value={form.recurrence_type} onChange={e => setForm({ ...form, recurrence_type: e.target.value })} style={{ padding: '0 8px', fontSize: '13px' }}>
                                            {RECURRENCE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div style={{ marginTop: '2px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input type="checkbox" checked={form.all_day} onChange={e => setForm({ ...form, all_day: e.target.checked })} id="add-all-day" style={{ cursor: 'pointer', width: '16px', height: '16px' }} />
                                        <label htmlFor="add-all-day" style={{ fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>Ganztägig</label>
                                    </div>
                                    {!form.all_day && (
                                        <div className="apple-time-grid" style={{ marginTop: '8px', padding: '8px', gap: '8px' }}>
                                            <div>
                                                <label className="apple-label" style={{ fontSize: '9px' }}>Start</label>
                                                <input type="time" className="apple-input" value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} style={{ height: '30px', fontSize: '12px', padding: '0 4px' }} />
                                            </div>
                                            <div>
                                                <label className="apple-label" style={{ fontSize: '9px' }}>Ende</label>
                                                <input type="time" className="apple-input" value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} style={{ height: '30px', fontSize: '12px', padding: '0 4px' }} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Bottom: Notes & Submit */}
                        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div>
                                <label className="apple-label">Notizen</label>
                                <textarea className="apple-notes-area" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optionale Notizen..." style={{ marginBottom: 0, minHeight: '32px' }} />
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                {editingId && (
                                    <button className="apple-btn-secondary" style={{ flex: 1, height: '38px', fontSize: '13px' }} onClick={handleReset}>
                                        Abbrechen
                                    </button>
                                )}
                                <button className="apple-btn-primary" style={{ flex: 1, height: '38px', fontSize: '13px' }} onClick={handleSubmit} disabled={isSaving || !form.startDate}>
                                    {isSaving ? 'Speichern...' : (editingId ? 'Speichern' : 'Hinzufügen')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>,
        document.body
    );
}

function getAvatarColor(name) {
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#14b8a6', '#06b6d4', '#3b82f6'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
}

function getInitials(name) {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}
